import { useState, useRef, ChangeEvent, SetStateAction, Dispatch, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Grid,
  Card,
  Alert,
  Stack,
  Button,
  Tooltip,
  Snackbar,
  Checkbox,
  TextField,
  IconButton,
  Typography,
  FormControl
} from "@material-ui/core";
import { Close, Download, InsertDriveFile, Upload, Edit, Restore } from "@material-ui/icons";
import { ALLOWED_FILE_MIME_TYPES } from "utils/fileValidation";
import { motion, AnimatePresence } from "framer-motion";
import { variants } from "./ListingImagesUpload";
import { preventBubbling } from "utils/listings/helper";
import { formatBytesToHumanReadableSize, getFileExtension } from "utils/listings/display";
import { LoadingButton } from "@material-ui/lab";
import { Link } from "react-router-dom";
import type { FormEvent } from "react";
import type { FilePreview } from "../../../../../@types/listings/fileData";
import type { SxProps } from "@material-ui/system";

type ListingFilesUploadProps = {
  loading?: boolean;
  snackbarOpen: boolean;
  filesPreview: FilePreview[];
  removeFile: (targetId: string) => () => void;
  handleFileUpload: ({ target: { files } }: ChangeEvent<HTMLInputElement>) => void;
  handleSnackbarClose: () => void;
};

/**
 * The `accept` of every listing-file input. Built from the list `getFilesData` validates
 * against, so the picker can never offer less than the upload accepts — the property
 * file's "קבצים" tab used to hardcode `image/*`, which hid every PDF from the picker.
 */
export const ACCEPTED_FILE_TYPES = ALLOWED_FILE_MIME_TYPES.join(", ");

const MotionGrid = motion(Grid);

export function ListingFilesUpload({
  loading,
  snackbarOpen,
  filesPreview,
  removeFile,
  handleFileUpload,
  handleSnackbarClose
}: ListingFilesUploadProps): JSX.Element {
  const { t: translate } = useTranslation();

  const inputFileRef = useRef<HTMLInputElement>(null);

  const handleClickInput = (): void => inputFileRef.current?.click();

  return (
    <Stack gap={2}>
      <input
        style={{ display: "none" }}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        ref={inputFileRef}
        multiple
        onChange={handleFileUpload}
      />
      <Snackbar
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: "100%" }}>
          {translate("listingsPage.warningFilesLimit")}
        </Alert>
      </Snackbar>
      <Button
        color="info"
        variant="contained"
        disabled={loading}
        startIcon={<Upload />}
        onClick={handleClickInput}
      >
        {translate("listingsPage.uploadFiles")}
      </Button>
      {!!filesPreview?.length && (
        <Grid container spacing={2}>
          <AnimatePresence>
            {filesPreview.map((filePreview) => (
              <MotionGrid
                item
                xs={12}
                sm={6}
                md={4}
                layout
                exit={variants.exit}
                initial={variants.initial}
                animate={variants.animate}
                key={filePreview.id}
              >
                <ListingFilePreviewCard
                  file={filePreview}
                  loading={loading}
                  removeFile={removeFile(filePreview.id)}
                />
              </MotionGrid>
            ))}
          </AnimatePresence>
        </Grid>
      )}
    </Stack>
  );
}

type ListingFilePreviewSelectProps = {
  checked: boolean;
  selectFile: () => void;
};

type ListingFilePreviewCardProps = {
  file: FilePreview;
  hidden?: boolean;
  loading?: boolean;
  selectProps?: ListingFilePreviewSelectProps;
  removeFile?: () => void;
  renameFile?: (newName: string) => Promise<void> | void;
  onLoadingChange?: Dispatch<SetStateAction<boolean>>;
};

export function ListingFilePreviewCard({
  file: { src, name, size, nameOverride: customName },
  hidden,
  loading,
  selectProps,
  removeFile,
  renameFile
}: ListingFilePreviewCardProps): JSX.Element {
  const { t: translate } = useTranslation();

  const [nameOverride, setNameOverride] = useState("");
  const [editing, setEditMode] = useState(false);

  const formRef = useRef<HTMLFormElement>(null);

  const rawNameOverride = customName;

  const parsedNameOverride = (rawNameOverride || name) ?? "";

  const trimmedNameOverride = nameOverride.trim();

  const isNameTheSame = name === trimmedNameOverride;

  useEffect(() => {
    setNameOverride(parsedNameOverride);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedNameOverride]);

  const handleDownload = async (): Promise<void> => {
    try {
      const response = await fetch(src);

      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }

      const blob = await response.blob();

      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");

      a.href = url;
      a.download = name;

      a.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
    }
  };

  const handleNameEdit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const isNameOverrideEmpty = !trimmedNameOverride;

    const skipRenaming = isNameOverrideEmpty || isNameTheSame;

    if (skipRenaming) {
      handleCancelEdit();
      return;
    }

    try {
      await renameFile(trimmedNameOverride);
    } finally {
      setEditMode(false);
    }
  };

  const handleRestoreOriginalName = async (): Promise<void> => {
    const isNameOverrideEmpty = !trimmedNameOverride;

    const skipRenaming = isNameOverrideEmpty || isNameTheSame;

    if (skipRenaming) {
      handleCancelEdit();
      return;
    }

    try {
      // Passing empty string makes it fall back to original name.
      await renameFile("");
    } finally {
      setEditMode(false);
    }
  };

  const handleCancelEdit = async (): Promise<void> => {
    setEditMode(false);
    setNameOverride(parsedNameOverride);
  };

  const handleToggleEditMode =
    (enable: boolean = true) =>
    (): void => {
      setEditMode(enable);
    };

  const handleNameOverrideChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>): void => {
    setNameOverride(value);
  };

  const handleRequestSubmit = (): void => {
    formRef.current?.requestSubmit();
  };

  const { checked, selectFile } = selectProps ?? {};

  const isDownloadButtonDisabled = hidden || loading;

  return (
    <Link
      to={hidden ? undefined : src}
      style={{
        textDecoration: "none",
        ...(hidden && {
          opacity: 0.5,
          pointerEvents: "none"
        })
      }}
      rel="noopener noreferrer"
      target="_blank"
      onClick={editing ? preventBubbling() : undefined}
    >
      <Card sx={{ p: 2 }} variant="outlined">
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
          <Stack sx={{ width: "100%", minWidth: 0 }} direction="row" alignItems="center" gap={1}>
            {selectProps && <Checkbox checked={checked} onClick={preventBubbling(selectFile)} />}
            <Box
              sx={{
                position: "relative",
                display: "grid",
                placeItems: "center"
              }}
            >
              <InsertDriveFile
                sx={{
                  width: 42,
                  height: 42
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  mt: "0.5rem",
                  color: "white",
                  fontWeight: "bold",
                  position: "absolute",
                  fontSize: "0.6rem"
                }}
              >
                {getFileExtension(name)}
              </Typography>
            </Box>
            <Stack sx={{ width: "100%" }} gap={2}>
              {editing ? (
                <FormControl fullWidth ref={formRef} component="form" onSubmit={handleNameEdit}>
                  <Stack sx={{ width: "100%" }} gap={1} alignItems="center">
                    <TextField
                      required
                      autoFocus
                      fullWidth
                      size="small"
                      value={nameOverride}
                      disabled={loading}
                      onChange={handleNameOverrideChange}
                    />
                    <Stack
                      sx={{ width: "100%" }}
                      gap={1}
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                    >
                      <Button
                        sx={{
                          ...defaultFullWidthStyles,
                          alignSelf: "start"
                        }}
                        size="small"
                        color="info"
                        variant="contained"
                        disabled={loading}
                        startIcon={<Restore />}
                        onClick={preventBubbling(handleRestoreOriginalName)}
                      >
                        {translate("listingsPage.control.restoreOriginalName")}
                      </Button>
                      <Stack direction="row" gap={1}>
                        <Button
                          sx={defaultFullWidthStyles}
                          size="small"
                          color="error"
                          variant="contained"
                          disabled={loading}
                          onClick={preventBubbling(handleCancelEdit)}
                        >
                          {translate("general.cancel")}
                        </Button>
                        <LoadingButton
                          sx={defaultFullWidthStyles}
                          type="submit"
                          size="small"
                          color="primary"
                          variant="contained"
                          loading={loading}
                          onClick={preventBubbling(handleRequestSubmit)}
                        >
                          {translate("general.confirm")}
                        </LoadingButton>
                      </Stack>
                    </Stack>
                  </Stack>
                </FormControl>
              ) : (
                <Box sx={{ display: "grid" }}>
                  <Typography title={name} variant="body2" noWrap>
                    {parsedNameOverride}
                  </Typography>
                  <Typography variant="caption">{formatBytesToHumanReadableSize(size)}</Typography>
                </Box>
              )}
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center">
            {!editing && renameFile && (
              <Tooltip title={translate("general.edit")}>
                <IconButton
                  size="small"
                  color="info"
                  onClick={preventBubbling(handleToggleEditMode(true))}
                  disabled={loading}
                >
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
            <Stack>
              {removeFile && (
                <Tooltip title={translate("listingsPage.control.delete")}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={preventBubbling(removeFile)}
                    disabled={loading}
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title={translate("listingsPage.control.download")}>
                <IconButton
                  size="small"
                  color="primary"
                  disabled={isDownloadButtonDisabled}
                  onClick={preventBubbling(handleDownload)}
                >
                  <Download />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Stack>
      </Card>
    </Link>
  );
}

const defaultFullWidthStyles: SxProps = {
  width: {
    xs: "100%",
    md: "initial"
  }
};
